import { useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/auth/AuthContext";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, Lock, KeyRound } from "lucide-react";

export default function LoginPage() {
  const { login, forgot } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // login | forgot
  const [forgotMsg, setForgotMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email.trim().toLowerCase(), password);
      } else {
        const res = await forgot(email.trim().toLowerCase());
        setForgotMsg(res?.message || "Si la cuenta existe, el administrador puede ayudarte.");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">
            Espíritu Travel · Centro de integración
          </p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight mt-1 flex items-center justify-center gap-3">
            CONECTOR
            <ArrowRight className="h-7 w-7 text-primary" strokeWidth={2.5} />
            KIMKIM
          </h1>
        </div>

        <Card className="shadow-none border-border">
          <CardHeader className="border-b border-border pb-3">
            <div className="flex items-center gap-2">
              {mode === "login" ? <Lock className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
              <h2 className="font-heading text-base font-bold tracking-tight">
                {mode === "login" ? "Acceso de agentes" : "Recuperar contraseña"}
              </h2>
            </div>
            <p className="text-xs text-muted-foreground">
              {mode === "login"
                ? "Inicia sesión con la cuenta que te facilite el administrador."
                : "Avisa al administrador para que te genere una nueva contraseña temporal."}
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nombre@viajadverdad.com"
                  required
                  className="rounded-md"
                />
              </div>
              {mode === "login" && (
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Contraseña</Label>
                  <Input
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="rounded-md"
                  />
                </div>
              )}
              {error && (
                <p className="text-sm text-red-600" data-testid="login-error">
                  {error}
                </p>
              )}
              {forgotMsg && (
                <p className="text-sm text-emerald-700" data-testid="forgot-success">
                  {forgotMsg}
                </p>
              )}
              <Button
                data-testid={mode === "login" ? "login-submit-button" : "forgot-submit-button"}
                type="submit"
                disabled={loading}
                className="w-full rounded-md font-semibold"
              >
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Procesando…</span>
                ) : mode === "login" ? (
                  "Iniciar sesión"
                ) : (
                  "Avisar al administrador"
                )}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                data-testid="toggle-forgot-button"
                onClick={() => {
                  setMode(mode === "login" ? "forgot" : "login");
                  setError("");
                  setForgotMsg("");
                }}
                className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              >
                {mode === "login" ? "¿Olvidaste tu contraseña?" : "Volver a iniciar sesión"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
